import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, from, forkJoin, firstValueFrom } from 'rxjs';
import { environment } from '../environments/environments';
import { CDPDeletePayload, CDPFlowNetworkPayload, CDPRowData } from './interfaces/cdp.interfaces';
import { ErrorHandler } from '../utils/error-handler';

@Injectable({
  providedIn: 'root',
})
export class CdpserviceService {
  constructor(private http: HttpClient) {}

  private baseUrl = environment.WebApiURL;
  private readonly cdpExportTabName = 'CDPFlowNetwork';

  private get cdpData() {
    return `${this.baseUrl}/api/FlowNetworkCDP/GetFlowNetworkCDPDetails`;
  }

  private get insertOrUpdateEndpoint() {
    return `${this.baseUrl}/api/FlowNetworkCDP/InsertOrUpdateFlowNetworkCDP`;
  }

  private get deleteEndpoint() {
    return `${this.baseUrl}/api/FlowNetworkCDP/RemoveCDPFlowNetwork`;
  }

  private get filterOptionsEndpoint() {
    return `${this.baseUrl}/api/FlowNetworkCDP/GetFilterFlowNetworkCDPMulti`;
  }
  private get lookupOptionsEndpoint() {
    return `${this.baseUrl}/api/FlowNetworkCDP/GetFilterFlowNetworkCDPDropDown`;
  }

  private get cdpCorridorDetailsEndpoint() {
    return `${this.baseUrl}/api/FlowNetworkCDP/GetCDPCorridorDetails`;
  }

  private get exportUrl() {
    return `${this.baseUrl}/api/FlowNetworkFE/ExportAllTabsData`;
  }

  private lastFilterUsed: any = null;

  async exportAllCDPFlowNetworkData(viewType: number): Promise<{ blob: Blob; fileName: string }> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const httpResponse = await firstValueFrom(
      this.http.post(this.exportUrl, { tabName: this.cdpExportTabName, view: viewType }, {
        headers,
        observe: 'response' as const,
        responseType: 'blob' as const,
      })
    );

    const blob = httpResponse.body;
    if (!blob) {
      throw new Error('Export returned an empty file.');
    }

    const contentDisposition = httpResponse.headers.get('content-disposition') || '';
    const contentType = httpResponse.headers.get('content-type') || blob.type || '';
    const fileNameMatch = contentDisposition.match(/filename\*?=(?:UTF-8''|"?)([^";]+)/i);
    const rawHeaderFileName = fileNameMatch?.[1]?.trim() || '';
    const decodedHeaderFileName = decodeURIComponent(rawHeaderFileName.replace(/^"|"$/g, ''));
    const normalizedContentType = contentType.toLowerCase();
    const inferredExtension =
      normalizedContentType.includes('spreadsheetml') ||
      normalizedContentType.includes('xlsx') ||
      decodedHeaderFileName.toLowerCase().endsWith('.xlsx')
        ? '.xlsx'
        : '.csv';
    const fallbackFileName = `cdp-flow-network${inferredExtension}`;
    const decodedFileName = rawHeaderFileName ? decodedHeaderFileName : fallbackFileName;
    const fileName = /\.[A-Za-z0-9]+$/.test(decodedFileName)
      ? decodedFileName
      : `${decodedFileName}${inferredExtension}`;

    return { blob, fileName };
  }

  getLookupOptions(
    filters: {
      cdp?: string | null;
      gasPlant?: string | null;
      gasStation?: string | null;
      ctb?: string | null;
      cs?: string | null;
      compressorStation?: string | null;
    } = {}
  ): Promise<any> {
    const payload = {
      cdp: filters.cdp ?? null,
      gasPlant: filters.gasPlant ?? filters.gasStation ?? null,
      ctb: filters.ctb ?? null,
      cs: filters.cs ?? filters.compressorStation ?? null,
    };

    return this.http.post(this.lookupOptionsEndpoint, payload).toPromise();
  }

  getCDPCorridorDetails(pseudoCDPName: string): Promise<any> {
    if (!pseudoCDPName) {
      return Promise.resolve(null);
    }
    const body = {
      developmentArea: null,
      facilityTypes: null,
      pseudoCDPNames: pseudoCDPName,
      cdpNames: null,
    };
    return firstValueFrom(
      this.http.post(this.cdpCorridorDetailsEndpoint, body)
    ).catch((err) => {
       
     
      return null;
    });
  }

  async getPaginatedCDPData(
    lastRowNumber: number,
    filters: {
      developmentArea?: string | null;
      padName?: string | null;
      wellPropID?: string | null;
      cdp?: string | null;
      gasPlant?: string | null;
      gasStation?: string | null;
      ctb?: string | null;
      satellite?: string | null;
      compressorStation?: string | null;
      wellName?: string | null;
    } = {},
    viewType: number = 1
  ): Promise<{ flowNetworkCDPDetails: any[]; nextRowNumber: number }> {
    this.lastFilterUsed = { ...filters, viewType };

    const payload = {
      lastRowNumber,
      view: viewType,
      developmentArea: filters.developmentArea ?? null,
      padName: filters.padName ?? null,
      wellPropID: filters.wellPropID ?? null,
      cdp: filters.cdp ?? null,
      gasPlant: filters.gasPlant ?? filters.gasStation ?? null,
      ctb: filters.ctb ?? null,
      sat: filters.satellite ?? null,
      cs: filters.compressorStation ?? null,
      wellName: filters.wellName ?? null,
    };

    try {
      const response = await firstValueFrom(
        this.http.post<any>(this.cdpData, payload)
      );

      const rows = response?.data || [];
      const nextRowNumber =
        response?.nextRowNumber ?? lastRowNumber + rows.length;

      return {
        flowNetworkCDPDetails: rows,
        nextRowNumber,
      };
    } catch (error) {
      return {
        flowNetworkCDPDetails: [],
        nextRowNumber: lastRowNumber,
      };
    }
  }

  async insertOrUpdateFlowNetworkCDP(payload: any): Promise<any> {
    try {
      const headers = new HttpHeaders().set('Content-Type', 'application/json');

      const response = await firstValueFrom(
        this.http.post<any>(this.insertOrUpdateEndpoint, payload, {
          headers,
        })
      );

      const parsed = ErrorHandler.parseApiResult(response);
      return {
        statusCode: parsed.statusCode,
        statusMessage: parsed.message,
        error: !parsed.success,
        ...response,
      };
    } catch (error: any) {
      const parsed = ErrorHandler.parseApiResult(error?.error || error);
      return {
        statusCode: parsed.statusCode || error?.status || 500,
        statusMessage: parsed.message,
        error: true,
      };
    }
  }

  async deleteFlowNetworkCDP(payload: CDPDeletePayload): Promise<any> {
    try {
      const headers = new HttpHeaders().set('Content-Type', 'application/json');

      const response = await firstValueFrom(
        this.http.post<any>(this.deleteEndpoint, payload, { headers })
      );

      // Handle nested statusMessage object
      if (
        response &&
        typeof response.statusMessage === 'object' &&
        response.statusMessage?.statusCode
      ) {
        const nested = response.statusMessage;
        const parsed = ErrorHandler.parseApiResult(nested);
        return {
          statusCode: parsed.statusCode,
          statusMessage: parsed.message,
          error: !parsed.success,
        };
      }

      const parsed = ErrorHandler.parseApiResult(response);
      return {
        statusCode: parsed.statusCode,
        statusMessage: parsed.message,
        error: !parsed.success,
        ...response,
      };
    } catch (error: any) {
      const parsed = ErrorHandler.parseApiResult(error?.error || error);
      return {
        statusCode: parsed.statusCode || error?.status || 500,
        statusMessage: parsed.message,
        error: true,
      };
    }
  }

  async getFilterOptions(
    columnName: string | null = null,
    viewType: number = 1
  ): Promise<any> {
    try {
      const payload = {
        columnName: columnName,
        view: viewType,
        developmentArea: null,
        padName: null,
        wellPropID: null,
        cdp: null,
        gasPlant: null,
        ctb: null,
        sat: null,
        cs: null,
        wellName: null,
      };

      const headers = new HttpHeaders().set('Content-Type', 'application/json');
      const response = await firstValueFrom(
        this.http.post<any>(this.filterOptionsEndpoint, payload, { headers })
      );

      return response || { flowNetworkFilterDetails: [] };
    } catch (error) {
      return { flowNetworkFilterDetails: [] };
    }
  }

  async getFilteredOptions(
    filters: any = {},
    viewType: number = 1
  ): Promise<any> {
    try {
      const payload = {
        columnName: null,
        view: viewType,
        developmentArea:
          typeof filters.developmentArea === 'string'
            ? filters.developmentArea
            : null,
        padName: typeof filters.padName === 'string' ? filters.padName : null,
        wellPropID:
          typeof filters.wellPropID === 'string' ? filters.wellPropID : null,
        cdp: typeof filters.cdp === 'string' ? filters.cdp : null,
        gasPlant:
          typeof filters.gasStation === 'string' ? filters.gasStation : null,
        ctb: typeof filters.ctb === 'string' ? filters.ctb : null,
        sat: typeof filters.satellite === 'string' ? filters.satellite : null,
        cs:
          typeof filters.compressorStation === 'string'
            ? filters.compressorStation
            : null,
        wellName:
          typeof filters.wellName === 'string' ? filters.wellName : null,
      };

      const headers = new HttpHeaders().set('Content-Type', 'application/json');
      const response = await firstValueFrom(
        this.http.post<any>(this.filterOptionsEndpoint, payload, { headers })
      );

      return response || { flowNetworkFilterDetails: [] };
    } catch (error) {
      return { flowNetworkFilterDetails: [] };
    }
  }

  async getSecondaryDataForRow(primaryRowData: any): Promise<any> {
    const payload = {
      lastRowNumber: primaryRowData.lastRowNumber || 0,
      view: 0,

      ctb: primaryRowData.ctb || 'NULL',
      cs: primaryRowData.compressorStation || 'NULL',
      cdp: primaryRowData.cdp || 'NULL',
      gasPlant: primaryRowData.gasPlant || primaryRowData.gasStation || null,

      developmentArea: null,
      padName: null,
      wellPropID: null,
      wellName: null,
      sat: null,
    };

    if (this.lastFilterUsed) {
      if (this.lastFilterUsed.developmentArea)
        payload.developmentArea = this.lastFilterUsed.developmentArea;
      if (this.lastFilterUsed.padName)
        payload.padName = this.lastFilterUsed.padName;
      if (this.lastFilterUsed.wellPropID)
        payload.wellPropID = this.lastFilterUsed.wellPropID;
      if (this.lastFilterUsed.wellName)
        payload.wellName = this.lastFilterUsed.wellName;
      if (this.lastFilterUsed.satellite)
        payload.sat = this.lastFilterUsed.satellite;
    }

    try {
      const response = await firstValueFrom(
        this.http.post<any>(this.cdpData, payload)
      );

      return response?.data || [];
    } catch (error) {
      return [];
    }
  }

  async refreshWithCurrentFilter(
    lastRowNumber: number = 0
  ): Promise<{ flowNetworkCDPDetails: any[]; nextRowNumber: number }> {
    if (this.lastFilterUsed) {
      return this.getPaginatedCDPData(
        lastRowNumber,
        this.lastFilterUsed,
        this.lastFilterUsed.viewType || 1
      );
    }

    return this.getPaginatedCDPData(lastRowNumber, {}, 1);
  }

  /**
   * Helper method to create CDP Flow Network payload
   * Eliminates duplicate payload creation code
   */
  public createFlowNetworkPayload(
    rowData: CDPRowData,
    action: 'INSERT' | 'UPDATE' | 'DELETE',
    userName: string
  ): CDPFlowNetworkPayload {
    return {
      oldCTB: rowData.original?.ctb || null,
      oldCS: rowData.original?.compressorStation || null,
      oldCDP: rowData.original?.cdp || null,
      oldGasPlant: rowData.original?.gasStation || null,

      newCTB: rowData.ctb || null,
      newCS: rowData.compressorStation || null,
      newCDP: rowData.cdp || null,
      newGasPlant: rowData.gasStation || null,
      action,
      userName,
    };
  }

  public createDeleteFlowNetworkPayload(rowData: CDPRowData, userName: string): CDPDeletePayload {
    return {
      ctb: rowData.ctb || '',
      cdp: rowData.cdp || '',
      gasPlant: rowData.gasStation || '',
      cs: rowData.compressorStation || '',
      userName,
    };
  }
}
